import type { InvoiceItem } from '../types/payments';
import {
  ServiceType,
  TowType,
  calculateDaysHeld,
  getBillingStartTime,
  getVehicleTypePrice,
  type Dispatch,
  type Impound,
  type Rate,
} from './calculations';

/**
 * The lines an invoice for a call starts with, priced from the call's
 * account's rates, following the dashboard's CreateInvoicePageV2: the tow's
 * base, its time, storage and labour. Shared by the old payment screen and
 * the mobile shell's create sheet, so a call is priced one way on both.
 *
 * Storage counts the days the vehicle has been held when the call has an
 * impound with a date, and one day otherwise. A rate that prices this
 * vehicle at nothing adds no line.
 */
export function lineItemsFromRates(
  rates: Rate[],
  call: Dispatch,
  impound: Impound | null
): InvoiceItem[] {
  const lines: InvoiceItem[] = [];
  const rate = (serviceType: ServiceType) =>
    rates.find((r) => r.serviceType === serviceType);

  const storageDays = impound
    ? calculateDaysHeld(impound.impoundDate, impound.releasedDate)
    : 1;
  const vehicleType = call.towType || TowType.ANY;

  const towBaseRate = rate(ServiceType.TOW_BASE);
  if (towBaseRate) {
    const price = getVehicleTypePrice(towBaseRate, vehicleType);
    if (price > 0) {
      lines.push({ description: 'Tow Base', quantity: 1, unitPrice: price });
    }
  }

  let towDurationInHours = 0;
  if (call.completionTime) {
    const startTime = getBillingStartTime(call);
    const endTime = new Date(call.completionTime);
    towDurationInHours =
      (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
  }

  const towHourMinimumRate = rate(ServiceType.TOW_HOUR_MINIMUM);
  const towQuarterHourRate = rate(ServiceType.TOW_QUARTER_HOUR);
  const towHalfHourRate = rate(ServiceType.TOW_HALF_HOUR);
  const towHourlyRate = rate(ServiceType.TOW_HOURLY);
  const towPerMileRate = rate(ServiceType.TOW_PER_MILE);

  if (towDurationInHours <= 1 && towHourMinimumRate) {
    const price = getVehicleTypePrice(towHourMinimumRate, vehicleType);
    if (price > 0) {
      lines.push({
        description: 'Tow Hour Minimum',
        quantity: 1,
        unitPrice: price,
      });
    }
  } else if (towDurationInHours > 1) {
    const remainingMinutes = towDurationInHours * 60;

    if (towHourlyRate) {
      const price = getVehicleTypePrice(towHourlyRate, vehicleType);
      if (price > 0) {
        const fullHours = Math.floor(remainingMinutes / 60);
        let remainingAfterHours = remainingMinutes - fullHours * 60;

        // A half hour or a quarter left over is billed as one more hour at
        // the hourly price, not at the half- or quarter-hour rate. That is
        // what the screen did; it is pinned by the tests, not endorsed.
        if (towHalfHourRate) {
          const halfHours = Math.floor(remainingAfterHours / 30);
          remainingAfterHours -= halfHours * 30;

          if (towQuarterHourRate) {
            const quarterHours = Math.ceil(remainingAfterHours / 15);
            lines.push({
              description: 'Tow Hourly',
              quantity:
                fullHours +
                (halfHours > 0 ? 1 : 0) +
                (quarterHours > 0 ? 1 : 0),
              unitPrice: price,
            });
          } else {
            lines.push({
              description: 'Tow Hourly',
              quantity: fullHours + (halfHours > 0 ? 1 : 0),
              unitPrice: price,
            });
          }
        } else {
          lines.push({
            description: 'Tow Hourly',
            quantity: Math.ceil(remainingMinutes / 60),
            unitPrice: price,
          });
        }
      }
    } else if (towHalfHourRate) {
      const price = getVehicleTypePrice(towHalfHourRate, vehicleType);
      if (price > 0) {
        lines.push({
          description: 'Tow Half Hour',
          quantity: Math.ceil(remainingMinutes / 30),
          unitPrice: price,
        });
      }
    } else if (towQuarterHourRate) {
      const price = getVehicleTypePrice(towQuarterHourRate, vehicleType);
      if (price > 0) {
        lines.push({
          description: 'Tow Quarter Hour',
          quantity: Math.ceil(remainingMinutes / 15),
          unitPrice: price,
        });
      }
    } else if (towPerMileRate) {
      const price = getVehicleTypePrice(towPerMileRate, vehicleType);
      if (price > 0) {
        lines.push({ description: 'Tow/Mile', quantity: 1, unitPrice: price });
      }
    }
  } else if (!towHourMinimumRate && towQuarterHourRate) {
    const price = getVehicleTypePrice(towQuarterHourRate, vehicleType);
    if (price > 0) {
      lines.push({
        description: 'Tow Quarter Hour',
        quantity: 1,
        unitPrice: price,
      });
    }
  }

  const storageBaseRate = rate(ServiceType.STORAGE_BASE);
  if (storageBaseRate) {
    const price = getVehicleTypePrice(storageBaseRate, vehicleType);
    if (price > 0) {
      lines.push({
        description: 'Storage Base',
        quantity: 1,
        unitPrice: price,
      });
    }
  }

  const storageDailyRate = rate(ServiceType.STORAGE_DAILY);
  if (storageDailyRate) {
    const price = getVehicleTypePrice(storageDailyRate, vehicleType);
    if (price > 0) {
      lines.push({
        description: 'Storage Daily',
        quantity: storageDays,
        unitPrice: price,
      });
    }
  }

  // Labour is rated by the hour and billed by the quarter.
  const laborRate = rate(ServiceType.LABOR);
  if (laborRate) {
    const price = getVehicleTypePrice(laborRate, vehicleType);
    if (price > 0) {
      lines.push({
        description: 'Labor Quarter Hour',
        quantity: 1,
        unitPrice: price / 4,
      });
    }
  }

  return lines;
}

/**
 * A call as the pricing reads it, priced by its tow type as the dashboard
 * prices it. The old screen read the vehicle's type, LIGHT_DUTY and the
 * like, which matches no rate column, so every call was billed at the
 * any-vehicle price.
 */
export function pricingCallOf(call: {
  id: string;
  account?: unknown;
  towType?: string | null;
  completionTime?: string | null;
  createdAt: string;
  startedAt?: string | null;
  arrivedAt?: string | null;
}): Dispatch {
  return {
    id: call.id,
    account: String(call.account ?? ''),
    towType: (call.towType as TowType) || TowType.ANY,
    completionTime: call.completionTime || undefined,
    createdAt: call.createdAt,
    startedAt: call.startedAt || undefined,
    arrivedAt: call.arrivedAt || undefined,
    mileage: undefined,
  };
}

/** UPPER_SNAKE as Title Case: TOW_BASE is "Tow Base". */
const serviceName = (serviceType: string): string =>
  serviceType
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** The services lineItemsFromRates prices itself, so not offered again. */
const PRICED_FROM_THE_CALL = [
  'Tow Base',
  'Tow Half Hour',
  'Tow Quarter Hour',
  'Tow Hourly',
  'Tow Hour Minimum',
  'Storage Base',
  'Storage Daily',
  'Labor Quarter Hour',
];

/**
 * The account's other rates, offered as charges to add with a tap: winching,
 * a gate fee. Each at the rate's price for any vehicle; one priced at
 * nothing for any vehicle is left out.
 */
export function quickCharges(
  rates: Rate[]
): { id: string; description: string; unitPrice: number }[] {
  return rates
    .filter(
      (rate) =>
        !PRICED_FROM_THE_CALL.includes(serviceName(rate.serviceType)) &&
        rate.any !== 0
    )
    .map((rate) => ({
      id: rate.id,
      description: serviceName(rate.serviceType),
      unitPrice: rate.any || 0,
    }));
}

/**
 * The lines with one more charge: a charge already on the invoice by that
 * name goes up by one instead of being listed twice.
 */
export function withCharge(
  lines: InvoiceItem[],
  charge: { description: string; unitPrice: number }
): InvoiceItem[] {
  const index = lines.findIndex(
    (line) =>
      line.description.toLowerCase() === charge.description.toLowerCase()
  );
  if (index < 0) return [...lines, { ...charge, quantity: 1 }];
  return lines.map((line, i) =>
    i === index ? { ...line, quantity: line.quantity + 1 } : line
  );
}
